import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAvailableDriversForMission } from '@/hooks/useDrivers';
import { useAvailableVehiclesForMission } from '@/hooks/useVehicles';
import { useAssignMission } from '@/hooks/useAssignMission';
import { toast } from 'sonner';
import { User, Truck } from 'lucide-react';

interface AssignMissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transportJobId: string;
  orderNumber: string;
  pickupDate?: string;
  deliveryDate?: string;
  currentVehicleId?: string;
  currentDriverId?: string;
}

export const AssignMissionDialog = ({
  open,
  onOpenChange,
  transportJobId,
  orderNumber,
  pickupDate,
  deliveryDate,
  currentVehicleId,
  currentDriverId,
}: AssignMissionDialogProps) => {
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');

  const isReassigning = !!currentVehicleId;
  
  const { availableDrivers, isLoading: loadingDrivers } = useAvailableDriversForMission(pickupDate, deliveryDate);
  const { availableVehicles, isLoading: loadingVehicles } = useAvailableVehiclesForMission(pickupDate, deliveryDate);
  const { assignMission, isAssigning } = useAssignMission();

  const handleAssign = async () => {
    if (!selectedDriverId) {
      toast.error('Veuillez sélectionner un chauffeur');
      return;
    }

    if (!isReassigning && !selectedVehicleId) {
      toast.error('Veuillez sélectionner un véhicule');
      return;
    }

    try {
      await assignMission({
        transportJobId,
        driverId: selectedDriverId,
        vehicleId: isReassigning ? currentVehicleId : selectedVehicleId,
      });
      toast.success(isReassigning ? 'Chauffeur modifié avec succès' : 'Mission assignée avec succès');
      onOpenChange(false);
      setSelectedDriverId('');
      setSelectedVehicleId('');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de l\'assignation');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#37699E] font-heading">
            {isReassigning ? 'Modifier le chauffeur' : 'Assigner la mission'}
          </DialogTitle>
          <DialogDescription className="text-[#5A6B7A]">
            Commande {orderNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Driver Selection */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide flex items-center gap-2">
              <User size={16} className="text-[#0880AE]" />
              Chauffeur
            </Label>
            <Select
              value={selectedDriverId}
              onValueChange={setSelectedDriverId}
              disabled={loadingDrivers || availableDrivers.length === 0}
            >
              <SelectTrigger className="rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                <SelectValue placeholder="Sélectionner un chauffeur" />
              </SelectTrigger>
              <SelectContent>
                {availableDrivers.map((driver) => (
                  <SelectItem key={driver.id} value={driver.id}>
                    {driver.users?.first_name} {driver.users?.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableDrivers.length === 0 && !loadingDrivers && (
              <p className="text-xs text-[#F2AC57]">Aucun chauffeur disponible</p>
            )}
          </div>

          {/* Vehicle Selection */}
          {!isReassigning && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide flex items-center gap-2">
                <Truck size={16} className="text-[#0880AE]" />
                Véhicule
              </Label>
              <Select
                value={selectedVehicleId}
                onValueChange={setSelectedVehicleId}
                disabled={loadingVehicles || availableVehicles.length === 0}
              >
                <SelectTrigger className="rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                  <SelectValue placeholder="Sélectionner un véhicule" />
                </SelectTrigger>
                <SelectContent>
                  {availableVehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.plate_number} - {vehicle.brand} {vehicle.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableVehicles.length === 0 && !loadingVehicles && (
                <p className="text-xs text-[#F2AC57]">Aucun véhicule disponible</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isAssigning}
            className="border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] rounded-lg"
          >
            Annuler
          </Button>
          <Button
            onClick={handleAssign}
            disabled={isAssigning || !selectedDriverId || (!isReassigning && !selectedVehicleId)}
            className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg"
          >
            {isAssigning ? 'En cours...' : 'Confirmer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
